import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Flame, TrendingUp, Users, ArrowUpRight, Briefcase, ChevronRight, Loader2 } from 'lucide-react';
import { featuresAPI } from '../utils/api';

const TrendingCareersWidget = () => {
    const [selectedCategory, setSelectedCategory] = useState('tech');
    const [trendingData, setTrendingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Fetch from backend
    useEffect(() => {
        const fetchTrending = async () => {
            setLoading(true);
            setError(false);
            try {
                const result = await featuresAPI.getTrendingCareers();
                if (result?.careers && result.careers.length > 0) {
                    // Group API data by category
                    const grouped = {};
                    result.careers.forEach(c => {
                        const cat = (c.category || 'tech').toLowerCase();
                        if (!grouped[cat]) grouped[cat] = [];
                        grouped[cat].push({
                            title: c.title,
                            demand: c.demand_growth || c.demand || 'N/A',
                            salary: c.salary_range || c.salary || 'N/A',
                            hot: c.is_hot || c.hot || false,
                        });
                    });
                    setTrendingData(grouped);
                } else {
                    setError(true);
                }
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchTrending();
    }, []);

    const categories = [
        { id: 'tech', label: 'Tech', icon: '⚡' },
        { id: 'data', label: 'Data', icon: '📊' },
        { id: 'design', label: 'Design', icon: '🎨' },
        { id: 'product', label: 'Product', icon: '🚀' },
    ];

    // Only show categories that have data
    const availableCategories = trendingData
        ? categories.filter(c => trendingData[c.id] && trendingData[c.id].length > 0)
        : categories;

    // Loading state
    if (loading) {
        return (
            <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Flame className="w-5 h-5 text-orange-400" />
                    <h3 className="font-bold text-white text-sm">Trending Careers</h3>
                </div>
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
                    <span className="ml-2 text-xs text-slate-500">Fetching market data...</span>
                </div>
            </div>
        );
    }

    // Error / empty state
    if (error || !trendingData) {
        return (
            <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Flame className="w-5 h-5 text-orange-400" />
                    <h3 className="font-bold text-white text-sm">Trending Careers</h3>
                </div>
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <TrendingUp className="w-8 h-8 text-slate-700 mb-3" />
                    <p className="text-sm text-slate-400 mb-1">Market data unavailable</p>
                    <p className="text-xs text-slate-600">Unable to fetch trending careers right now. Please try again later.</p>
                </div>
            </div>
        );
    }

    const currentData = trendingData[selectedCategory] || [];

    return (
        <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-400" />
                    <h3 className="font-bold text-white text-sm">Trending Careers</h3>
                    <span className="text-[10px] font-mono text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full animate-pulse">LIVE</span>
                </div>
            </div>

            {/* Category Tabs */}
            <div className="flex gap-2 mb-4">
                {availableCategories.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`text-xs px-3 py-1.5 rounded-lg transition-all ${selectedCategory === cat.id
                                ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                                : 'text-slate-500 hover:text-slate-300 border border-transparent'
                            }`}
                    >
                        {cat.icon} {cat.label}
                    </button>
                ))}
            </div>

            {/* Career List */}
            <div className="space-y-2">
                {currentData.map((career, i) => (
                    <motion.div
                        key={career.title}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-center justify-between p-3 bg-[#0B0F19] border border-white/5 rounded-xl hover:border-white/10 transition-colors group"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center">
                                <Briefcase className="w-4 h-4 text-orange-400" />
                            </div>
                            <div>
                                <div className="text-sm text-white font-medium flex items-center gap-2">
                                    {career.title}
                                    {career.hot && (
                                        <span className="text-[9px] font-mono text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">🔥 HOT</span>
                                    )}
                                </div>
                                <div className="text-[10px] text-slate-500 font-mono">{career.salary}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-emerald-400 font-mono font-bold">{career.demand}</span>
                            <ArrowUpRight className="w-3 h-3 text-slate-600 group-hover:text-emerald-400 transition-colors" />
                        </div>
                    </motion.div>
                ))}
                {currentData.length === 0 && (
                    <p className="text-xs text-slate-600 text-center py-4">No careers in this category yet</p>
                )}
            </div>
        </div>
    );
};

export default TrendingCareersWidget;
